export const MOUNT_ENGINES = ['postgres', 'sqlserver'];
export const MOUNT_ENGINE_LABELS = {
    postgres: 'PostgreSQL',
    sqlserver: 'SQL Server',
};
/**
 * The shared mount instances (infra/lib/compute.nested-stack.ts,
 * `atlas-mount-postgres` and `atlas-mount-sqlserver`). The ids are the ones
 * CRM assignments and existing jobs (`rds.databaseId`) already reference.
 * Host, port and credentials live in the instance's generated Secrets Manager
 * secret, never in code or in the settings table.
 */
export const MOUNT_DATABASES = [
    { id: 'postgres-shared', label: 'PostgreSQL (shared)', engine: 'postgres' },
    { id: 'sqlserver-shared', label: 'SQL Server (shared)', engine: 'sqlserver' },
];
/**
 * Parses an RDS generated master secret
 * (`{"username","password","host","port",…}`). Throws on a missing field.
 */
export function parseMountDatabaseSecret(raw) {
    const parsed = JSON.parse(raw);
    const host = parsed['host'];
    const port = Number(parsed['port']);
    const username = parsed['username'];
    const password = parsed['password'];
    if (typeof host !== 'string' ||
        !host ||
        !Number.isInteger(port) ||
        port <= 0 ||
        typeof username !== 'string' ||
        !username ||
        typeof password !== 'string') {
        throw new Error('Mount database secret must hold host, port, username and password');
    }
    return { host, port, username, password };
}
/**
 * The catalogue of mount methods, keyed by the source file type they handle.
 * Add new methods here as more file types/processing pipelines are supported.
 */
export const MOUNT_METHODS = [
    {
        id: 'bak',
        label: 'SQL Server .bak restore',
        description: 'Restores a SQL Server .bak backup from S3 directly into the target RDS instance via native restore.',
    },
    {
        id: 'dump',
        label: 'PostgreSQL dump restore',
        description: 'Restores a PostgreSQL dump from S3 into the target RDS instance via pg_restore.',
    },
    {
        id: 'sql',
        label: 'PostgreSQL SQL dump (psql)',
        description: 'Loads a plain-text PostgreSQL dump (pg_dump default/plain format) into the target RDS instance via psql. Used for sources like Vincere that deliver a zipped or gzipped plain-SQL dump rather than a pg_restore custom-format archive.',
    },
    {
        id: 'csv',
        label: 'CSV → PostgreSQL',
        description: 'Loads every CSV file in the source folder into the target PostgreSQL database, one table per file.',
    },
    {
        id: 'loxo',
        label: 'Loxo JSONL → PostgreSQL',
        description: 'Parses Loxo JSONL exports (flattening nested objects, expanding arrays into child tables, and building derived tables) into the target PostgreSQL database.',
    },
];
/** Looks up a mount method definition by id. */
export function findMountMethod(id) {
    return MOUNT_METHODS.find((m) => m.id === id);
}
/**
 * The schemas of a mounted database that are copied to Redshift. Bullhorn's
 * .bak restores 3–5 schemas but only `bullhorn1` is wanted (matched
 * case-insensitively); without a match, every schema, rather than none.
 */
export function syncedSchemas(schemas, sourceSystem) {
    if (sourceSystem !== 'bullhorn')
        return schemas;
    const matched = schemas.filter((s) => s.toLowerCase() === 'bullhorn1');
    return matched.length ? matched : schemas;
}
/**
 * Default CRM assignments. Bullhorn produces a SQL Server .bak, so it uses the
 * .bak method into the SQL Server instance; everything else starts unassigned.
 */
export const DEFAULT_CRM_ASSIGNMENTS = {
    bullhorn: { methodId: 'bak', databaseId: 'sqlserver-shared' },
    // Thrive delivers a password-protected zip containing a PostgreSQL dump, which
    // the worker extracts to unzipped/ and restores via pg_restore.
    thrive: { methodId: 'dump', databaseId: 'postgres-shared' },
    // Loxo delivers JSONL exports parsed into the shared Postgres instance.
    loxo: { methodId: 'loxo', databaseId: 'postgres-shared' },
    // Vincere delivers a zipped or gzipped plain-SQL pg_dump: the main
    // `…-database-…` dump plus a smaller `…-calcdb-…` mail DB. The import worker
    // extracts both to unzipped/; the sql method loads the main dump via psql into
    // the client DB and the calcdb dump into a companion `<client>_calcdb` DB (its
    // own Redshift schema). The two share table names (user_account, shift, …) so
    // they cannot live in one database.
    vincere: { methodId: 'sql', databaseId: 'postgres-shared' },
    // Recruit CRM delivers CSV files loaded into the shared Postgres instance.
    recruit_crm: { methodId: 'csv', databaseId: 'postgres-shared' },
    // JobAdder delivers an unencrypted SQL Server .bak (restored via the bak
    // method); its monthly document zips are always unzipped to S3 by the worker
    // for a separate downstream process.
    jobadder: { methodId: 'bak', databaseId: 'sqlserver-shared' },
};
//# sourceMappingURL=crm.js.map